import { ApprovalScopeEnum, CostCenter } from "@finway-group/shared/lib/models"
import { Employee, GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { FINWAY_ADMIN_EMAIL } from "~Shared/config/consts"
import { ApprovalProcessService, AuthzService, CurrencyService, ExpenseService } from "~Shared/services"
import { addSuperiorToApproverList, ensureNextApproverInDropdown } from "~Shared/utils/expense.utils"
import { PurchaseOrder, PurchaseOrderFormValues } from "../types/PurchaseOrder.interfaces"
import { addCostCenterResponsible } from "../types/utils/helper.util"

export const PurchaseOrderService = {
    determinePossibleApproversForPurchaseOrder: ({
        purchaseOrder,
        employees,
        costCenters,
        loggedInUser,
        fallbackApproverId,
    }: {
        purchaseOrder: PurchaseOrderFormValues
        employees: Array<Employee>
        costCenters: Array<CostCenter>
        loggedInUser: Employee
        fallbackApproverId: string
    }) => {
        const requesterId = purchaseOrder.requestedBy._id ? purchaseOrder.requestedBy._id : loggedInUser.id

        const requestedBy = GetEmployeeById(employees, requesterId)

        const purchaseOrderPrice = loggedInUser.settings.showGrossAmount ? purchaseOrder.totalGrossPrice : purchaseOrder.totalNetPrice

        // filter requester is by default set to true for expenses approval, and set to false for expense tagging list
        let filteredEmployees = employees.filter((employee) => !employee.activeCompanyProfile?.deleted && AuthzService.isUserPurchaseOrderApprover(employee.id))

        // if not in the requester's limits and if the request's payment option is smart card, remove auto approver
        if (requestedBy) {
            const limit = ExpenseService.getAutoApprovalLimit(employees, requestedBy, purchaseOrder.currency)
            if (!limit || limit < purchaseOrderPrice) {
                filteredEmployees = filteredEmployees.filter(({ email }) => email !== FINWAY_ADMIN_EMAIL)
            }
        }

        // also add the superior to the list
        if (requestedBy?.activeCompanyProfile.superior) {
            filteredEmployees = addSuperiorToApproverList({ superiorId: requestedBy.activeCompanyProfile.superior, employees, filteredEmployees })
        }

        // for preapproved, also add the CC responsible to the list
        filteredEmployees = addCostCenterResponsible({ filteredEmployees, employees, costCenters, purchaseOrder })

        // requester should not be a possible approver himself
        if (requestedBy && requestedBy.id !== fallbackApproverId) {
            filteredEmployees = filteredEmployees.filter((employee) => employee?.id !== requestedBy.id)
        }

        // lastly remove all that are listed in the limits and we already exceeded their approval limit
        filteredEmployees = PurchaseOrderService.removeEmployeeWithExceedLimitFromApprovers({ filteredEmployees, purchaseOrder, purchaseOrderPrice, requestedBy })

        // This will make sure that the next approver of current approval process is shown in the dropdown.
        filteredEmployees = purchaseOrder?.approvalProcesses?.length
            ? ensureNextApproverInDropdown({ approvalProcesses: purchaseOrder.approvalProcesses, employees, filteredEmployees })
            : filteredEmployees

        if (!filteredEmployees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)) {
            const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)
            filteredEmployees.push(autoApprover!)
        }

        return filteredEmployees
    },

    removeEmployeeWithExceedLimitFromApprovers: ({
        filteredEmployees,
        requestedBy,
        purchaseOrder,
        purchaseOrderPrice,
    }: {
        filteredEmployees: Array<Employee>
        purchaseOrder: PurchaseOrderFormValues
        purchaseOrderPrice: number
        requestedBy?: Employee
    }) =>
        filteredEmployees.filter((employee: Employee) => {
            if (!employee) {
                return false
            }
            if (AuthzService.isGlobalApprover(employee.activeCompanyProfile.roleId) && employee.email !== FINWAY_ADMIN_EMAIL) {
                return true
            }

            const foundInApproval = requestedBy?.activeCompanyProfile.approvals?.find((approval) => approval.approver === employee.id)

            if (foundInApproval) {
                const limit =
                    foundInApproval.currency === purchaseOrder.currency
                        ? foundInApproval.limit
                        : CurrencyService.convert(foundInApproval.limit, foundInApproval.currency, purchaseOrder.currency)
                return limit >= purchaseOrderPrice
            }
            return true
        }),

    confirmAdminNotOwnApproval: async (purchaseOrder: PurchaseOrder, user: Employee) => {
        const nextApprover = await ApprovalProcessService.getNextApprover(purchaseOrder.approvalProcesses)
        return ApprovalProcessService.confirmAdminNotOwnApproval(purchaseOrder.approvalProcesses, ApprovalScopeEnum.PURCHASE_ORDER, user, nextApprover?.name ?? "", "purchaseOrder")
    },
}
